import axios from "axios";
import { config } from "../config";
import { history } from "react-router-guard";
import { educationRoutes } from "../modules/educations/types/education.routes";

const ROUTE_PREFIX = "education/";

export class CoursesService {
  private readonly serverUrl = config.serverUrl;
  // Course API's
  public list = async (params?: any): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}courses`,
        { params },
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public add = async (data: any): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}courses`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public update = async (id: string, data: any): Promise<any> => {
    try {
      const response = await axios.put(
        this.serverUrl + `${ROUTE_PREFIX}courses/${id}`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public get = async (id: string): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}courses/${id}`,
      );
      return response.data;
    } catch (err: any) {
      history.push(educationRoutes.courses);
      return {};
    }
  };

  public delete = async (id: string): Promise<any> => {
    try {
      const response = await axios.delete(
        this.serverUrl + `${ROUTE_PREFIX}courses/${id}`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // Document API's
  public addDocument = async (courseId: string, data: any): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}courses/${courseId}/documents`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public deleteDocument = async (
    courseId: string,
    id: number,
  ): Promise<any> => {
    try {
      const response = await axios.delete(
        this.serverUrl + `${ROUTE_PREFIX}courses/${courseId}/documents/${id}`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // Lesson API's
  public lessons = async (courseId: string, params?: any): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}lessons`,
        { params },
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public deleteLesson = async (id: string): Promise<any> => {
    try {
      const response = await axios.delete(
        this.serverUrl + `${ROUTE_PREFIX}lessons/${id}`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public getLesson = async (lessonId: string): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}lessons/${lessonId}`,
      );
      return response.data;
    } catch (err: any) {
      history.push(educationRoutes.courses);
      return {};
    }
  };

  public addLesson = async (data: any): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}lessons`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public updateLesson = async (lessonId: string, data: any): Promise<any> => {
    try {
      const response = await axios.put(
        this.serverUrl + `${ROUTE_PREFIX}lessons/${lessonId}`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public lessonsBySectionId = async (
    sectionId: string,
    params?: any,
  ): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}lessons/section/${sectionId}`,
        { params },
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // Lesson ProgressAPI's
  public startLesson = async (lessonId: string): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}lessons/${lessonId}/start`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public updateLessonProgress = async (
    lessonId: string,
    progressSeconds: number,
  ): Promise<any> => {
    try {
      const response = await axios.patch(
        this.serverUrl + `${ROUTE_PREFIX}lessons/${lessonId}/progress`,
        {
          progress_seconds: Math.max(0, Math.floor(progressSeconds)),
        },
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public completeLesson = async (lessonId: string): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}lessons/${lessonId}/complete`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // Section API's
  public listSections = async (
    courseId: string,
    params?: any,
  ): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}sections/course/${courseId}`,
        { params },
      );
      return response.data;
    } catch (err: any) {
      return [];
    }
  };

  // Section API's
  public getLearnerSection = async (id: string): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}sections/${id}/learner`,
      );
      return response.data;
    } catch (err: any) {
      return [];
    }
  };

  public addSection = async (data: Record<string, unknown>): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}sections`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public updateSection = async (
    sectionId: string,
    data: Record<string, unknown>,
  ): Promise<any> => {
    try {
      const response = await axios.put(
        this.serverUrl + `${ROUTE_PREFIX}sections/${sectionId}`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public deleteSection = async (sectionId: string): Promise<any> => {
    try {
      const response = await axios.delete(
        this.serverUrl + `${ROUTE_PREFIX}sections/${sectionId}`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // Checkpoint API's
  public addCheckpoint = async (
    data: Record<string, unknown>,
  ): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}checkpoints`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public updateCheckpoint = async (
    checkpointId: string,
    data: Record<string, unknown>,
  ): Promise<any> => {
    try {
      const response = await axios.put(
        this.serverUrl + `${ROUTE_PREFIX}checkpoints/${checkpointId}`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public deleteCheckpoint = async (checkpointId: string): Promise<any> => {
    try {
      const response = await axios.delete(
        this.serverUrl + `${ROUTE_PREFIX}checkpoints/${checkpointId}`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public getCheckpoint = async (checkpointId: string): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}checkpoints/${checkpointId}`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // question API's
  public addCheckpointQuestions = async (
    checkpointId: string,
    data: any,
  ): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}checkpoints/${checkpointId}/questions`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public getCheckpointExam = async (examId: string): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}checkpoints/${examId}/exam`,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  public submitCheckpointExam = async (
    examId: string,
    data: Record<string, unknown>,
  ): Promise<any> => {
    try {
      const response = await axios.post(
        this.serverUrl + `${ROUTE_PREFIX}checkpoints/${examId}/exam/submit`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // Course Students API's
  public courseStudents = async (
    courseId: string,
    params?: any,
  ): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}courses/${courseId}/finished-users`,
        { params },
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // Lesson Students API's
  public lessonStudents = async (
    lessonId: string,
    params?: any,
  ): Promise<any> => {
    try {
      const response = await axios.get(
        this.serverUrl + `${ROUTE_PREFIX}lessons/${lessonId}/finished-users`,
        { params },
      );
      return response.data;
    } catch (err: any) {
      return {};
    }
  };

  // Lesson Duration API's
  public getLessonDuration = async (videoUrl: string): Promise<any> => {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`,
    );
    if (!response.ok) {
      throw new Error("Could not fetch Vimeo metadata");
    }
    const data = await response.json();
    console.log(data);
    return Number(data.duration) || 0;
  };
}
